import Controller from '@ember/controller';
import { service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { sortByProperties } from 'donor-elf-web/lib/array-utils';
import AddSomethingModal, {
  type PledgeSavedArgs,
} from 'donor-elf-web/services/add-something-modal';
import moment from 'moment';

export default class YtdGivenExpectingController extends Controller {
  @service declare addSomethingModal: AddSomethingModal;

  savePledgeSubsciptionToken?: string;

  @action
  templateDidInsert() {
    this.savePledgeSubsciptionToken =
      this.addSomethingModal.subscribeToPledgeSaved(this.handlePledgeSaved);
  }

  @action
  templateWillDestroy() {
    if (this.savePledgeSubsciptionToken) {
      this.addSomethingModal.unsubscribeWithToken(
        this.savePledgeSubsciptionToken,
      );
    }
  }

  @action
  async handlePledgeSaved(_msg: string, _data: PledgeSavedArgs) {
    this.send('refreshYtdGivenExpectingRoute');
  }

  @tracked contactExpectedItemsSortBy: { property: string; direction: string } =
    {
      property: 'totalExpecting',
      direction: 'desc',
    };

  @tracked contactOverdueItemsSortBy: { property: string; direction: string } =
    {
      property: 'totalExpecting',
      direction: 'desc',
    };

  @tracked contactNoGiftItemsSortBy: { property: string; direction: string } = {
    property: 'totalExpecting',
    direction: 'desc',
  };

  get sortedContactExpectedItems() {
    return sortByProperties(this.model.contactExpectedItems, [
      `${this.contactExpectedItemsSortBy.property}:${this.contactExpectedItemsSortBy.direction}`,
    ]);
  }

  get sortedContactOverdueItems() {
    return sortByProperties(this.model.contactOverdueItems, [
      `${this.contactOverdueItemsSortBy.property}:${this.contactOverdueItemsSortBy.direction}`,
    ]);
  }

  get sortedContactNoGiftItems() {
    return sortByProperties(this.model.contactNoGiftItems, [
      `${this.contactNoGiftItemsSortBy.property}:${this.contactNoGiftItemsSortBy.direction}`,
    ]);
  }

  get report() {
    return this.model.givenExpectedReport;
  }

  get givenAmount() {
    return this.report.ytdData.given_amount;
  }

  get givenPercent() {
    return this.report.ytdData.given_percent;
  }

  get expectedAmount() {
    return this.report.ytdData.expected_amount;
  }

  get expectedPercent() {
    return this.report.ytdData.expected_percent;
  }

  get noGiftsAmount() {
    return this.report.ytdData.no_gifts_amount;
  }

  get noGiftsPercent() {
    return this.report.ytdData.no_gifts_percent;
  }

  get overdueAmount() {
    return this.report.ytdData.overdue_amount;
  }

  get overduePercent() {
    return this.report.ytdData.overdue_percent;
  }

  get totalAmount() {
    return this.report.ytdData.total_amount;
  }

  get totalPercent() {
    return this.report.ytdData.total_percent;
  }

  get leftAmount() {
    return this.report.ytdData.left_amount;
  }

  get leftPercent() {
    return this.report.ytdData.left_percent;
  }

  get givenFrom() {
    return moment(this.report.startAt).format('YYYY-MM-DD');
  }

  get givenTo() {
    return this.report.ytdData.given_to_date;
  }

  get pledgesFrom() {
    return this.report.ytdData.expected_from_date;
  }

  get pledgesTo() {
    return this.report.ytdData.expected_to_date;
  }

  get excludeNoGiftPledges() {
    return this.report.ytdData.exclude_no_gift_pledges || false;
  }

  @action
  sortContactExpectedItemsBy(sortProperty: string) {
    if (sortProperty === this.contactExpectedItemsSortBy.property) {
      this.contactExpectedItemsSortBy = {
        property: sortProperty,
        direction:
          this.contactExpectedItemsSortBy.direction === 'asc' ? 'desc' : 'asc',
      };
    } else {
      this.contactExpectedItemsSortBy = {
        property: sortProperty,
        direction: 'asc',
      };
    }
  }

  @action
  sortContactOverdueItemsBy(sortProperty: string) {
    if (sortProperty === this.contactOverdueItemsSortBy.property) {
      this.contactOverdueItemsSortBy = {
        property: sortProperty,
        direction:
          this.contactOverdueItemsSortBy.direction === 'asc' ? 'desc' : 'asc',
      };
    } else {
      this.contactOverdueItemsSortBy = {
        property: sortProperty,
        direction: 'asc',
      };
    }
  }

  @action
  sortContactNoGiftItemsBy(sortProperty: string) {
    if (sortProperty === this.contactNoGiftItemsSortBy.property) {
      this.contactNoGiftItemsSortBy = {
        property: sortProperty,
        direction:
          this.contactNoGiftItemsSortBy.direction === 'asc' ? 'desc' : 'asc',
      };
    } else {
      this.contactNoGiftItemsSortBy = {
        property: sortProperty,
        direction: 'asc',
      };
    }
  }
}
